import type {
  PrinterAdapter,
  PrinterDevice,
  PrinterSession,
  PrinterStatus,
  LabelTemplate,
} from '@trackeat/shared';
import { Capacitor } from '@capacitor/core';
import QRCode from 'qrcode';
import { LabelPrintStageError } from '../../labels/printError.js';
import { renderTemplate, type RenderedElement } from './renderer.js';
import { isY428Printer, tsplProfile } from './printerProfile.js';
import { buildTsplBitmapJob } from './labelRaster.js';
import { bleConnect, bleDisconnect, bleWriteBytes, bleScanPrinters } from './ble-transport.js';
import {
  classicConnect, classicForceDisconnect, classicWriteBytes,
  classicListPaired,
} from './bt-classic-transport.js';

// Su Android usiamo Bluetooth Classic SPP (RFCOMM) per le stampanti tipo
// Clabel CT221B: replica esattamente il pairing/connessione dell'app del
// produttore. Su iOS / PWA torniamo a BLE.
// T-081 follow-up: il transport è BT Classic SPP solo se:
//   1. siamo su Android (iOS / web non hanno SPP), E
//   2. il device NON è dichiarato BLE.
// Senza il check (2), un Niimbot pairato come BLE su Android veniva
// trattato come SPP e il connect falliva con errore generico → l'utente
// vedeva "stampante non si collega".
const useClassic = (connection?: string): boolean =>
  Capacitor.getPlatform() === 'android' && connection !== 'ble';

const DPI = 203;
const MM_PER_INCH = 25.4;
const mmToDots = (mm: number): number => Math.round((mm / MM_PER_INCH) * DPI);

// La testina Clabel CT221B non riconosce UTF-8 multi-byte: i caratteri
// accentati italiani (à è é ì ò ù) finiscono come ideogrammi cinesi.
// Rimuoviamo gli accenti prima di inviare. Il valore originale resta intatto
// nel DB e nella trace-page (HTML, UTF-8 nativo).
const stripAccents = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '');

const escapeTspl = (s: string): string =>
  stripAccents(s).replace(/"/g, "'").replace(/\n/g, ' ');

/**
 * Mappa font_size_pt → bitmap font TSPL.
 * Clabel CT221B (203 DPI) ha font bitmap fissi:
 *   "0" ≈ 4pt (8x12 dot, ultra small)
 *   "1" ≈ 7pt (12x20 dot)
 *   "2" ≈ 10pt (16x24 dot)
 *   "3" ≈ 14pt (24x32 dot)
 *   "4" ≈ 18pt (32x48 dot)
 *   "5" ≈ 24pt (48x72 dot)
 * NB: la dimensione richiesta in pt nell'editor viene mappata al font
 * bitmap PIÙ VICINO; non c'è interpolazione perché TSPL usa font ROM.
 */
const pickTsplFont = (pt: number | undefined): string => {
  if (!pt) return '1';
  if (pt <= 5)  return '0'; // ultra-small per ingredienti/allergeni stretti
  if (pt <= 8)  return '1';
  if (pt <= 11) return '2';
  if (pt <= 15) return '3';
  if (pt <= 20) return '4';
  return '5';
};

// Offset additivo applicato alle coordinate di OGNI elemento. Il comando
// REFERENCE di TSPL viene ignorato (o interpretato al contrario) dal
// firmware Clabel CT221B: alzando REFERENCE la stampa si shifta verso
// l'angolo top-left invece che destra/giù. Sommando manualmente l'offset
// alle coordinate ci assicuriamo che la direzione sia corretta.
const TSPL_OFFSET_X_DOTS = 8; // 1 mm shift verso destra
const TSPL_OFFSET_Y_DOTS = 4; // 0.5 mm shift verso il basso

const buildText = (el: RenderedElement): string => {
  const x = mmToDots(el.x_mm) + TSPL_OFFSET_X_DOTS;
  const y = mmToDots(el.y_mm) + TSPL_OFFSET_Y_DOTS;
  const font = pickTsplFont(el.font_size_pt);
  const rot = el.rotation;
  const text = escapeTspl(el.text ?? '');
  const cmd = `TEXT ${x},${y},"${font}",${rot},1,1,"${text}"`;
  // Bold: TSPL bitmap fonts non hanno glyph bold nativo. Trucco standard
  // dell'industria etichette = "overprint shift" — stampiamo lo stesso
  // testo una seconda volta shiftato di 1 dot in X. Crea un effetto
  // pseudo-bold visivamente leggibile mantenendo il bitmap font.
  if (el.font_weight === 'bold') {
    return cmd + '\r\n' + `TEXT ${x + 1},${y},"${font}",${rot},1,1,"${text}"`;
  }
  return cmd;
};

const buildBarcode = (el: RenderedElement): string => {
  const x = mmToDots(el.x_mm) + TSPL_OFFSET_X_DOTS;
  const y = mmToDots(el.y_mm) + TSPL_OFFSET_Y_DOTS;
  const h = el.h_mm ? mmToDots(el.h_mm) : 60;
  const rot = el.rotation;
  const data = escapeTspl(el.barcode_data ?? '');
  return `BARCODE ${x},${y},"128",${h},1,${rot},2,2,"${data}"`;
};

// Numero di moduli per lato del QR (es. 21 per versione 1, 37 per la 5).
// Dipende da lunghezza dato + livello ECC, NON dalla larghezza richiesta:
// per dimensionare il QR alla w_mm del template dobbiamo conoscerlo.
const qrModuleCount = (data: string): number => {
  try {
    const sym = (QRCode as unknown as {
      create: (d: string, o: { errorCorrectionLevel: string }) => {
        modules: { size: number };
      };
    }).create(data, { errorCorrectionLevel: 'L' });
    return sym.modules.size;
  } catch {
    // Fallback prudente: versione 5 (37 moduli) ≈ trace_url tipico.
    return 37;
  }
};

const buildQr = (el: RenderedElement): string => {
  const x = mmToDots(el.x_mm) + TSPL_OFFSET_X_DOTS;
  const y = mmToDots(el.y_mm) + TSPL_OFFSET_Y_DOTS;
  const rot = el.rotation;
  const data = escapeTspl(el.qr_data ?? '');
  // La dimensione fisica stampata = cellWidth (dot/modulo) × numero moduli.
  // Il numero di moduli dipende dal dato (URL di tracciabilità ~110 char) e
  // dal livello ECC, quindi NON possiamo derivare cellWidth da w_mm con una
  // costante fissa (il vecchio `w_mm/5` ignorava i moduli: cambiare la
  // larghezza nel template non aveva alcun effetto). Calcoliamo i moduli
  // reali con la lib qrcode (stesso ECC "L" del comando QRCODE) e scegliamo
  // la cellWidth che riempie la larghezza richiesta.
  const modules = qrModuleCount(data);
  const targetDots = el.w_mm ? mmToDots(el.w_mm) : mmToDots(20);
  // Minimo 4 dot/modulo: sotto i moduli si fondono su testine 203 DPI e la
  // stampa non scansiona più (invariante in CLAUDE.md). Max 12 di sicurezza.
  const cellWidth = Math.max(4, Math.min(12, Math.round(targetDots / modules)));
  return `QRCODE ${x},${y},L,${cellWidth},A,${rot},"${data}"`;
};

const buildBar = (el: RenderedElement): string => {
  const x = mmToDots(el.x_mm) + TSPL_OFFSET_X_DOTS;
  const y = mmToDots(el.y_mm) + TSPL_OFFSET_Y_DOTS;
  const w = mmToDots(el.w_mm ?? 0);
  const h = Math.max(1, mmToDots(el.h_mm ?? 0.3));
  return `BAR ${x},${y},${w},${h}`;
};

const buildBox = (el: RenderedElement): string => {
  const x = mmToDots(el.x_mm) + TSPL_OFFSET_X_DOTS;
  const y = mmToDots(el.y_mm) + TSPL_OFFSET_Y_DOTS;
  const x2 = x + mmToDots(el.w_mm ?? 0);
  const y2 = y + mmToDots(el.h_mm ?? 0);
  return `BOX ${x},${y},${x2},${y2},2`;
};

export const buildTspl = (
  template: LabelTemplate,
  data: Record<string, unknown>,
  copies: number = 1,
): string => {
  const rendered = renderTemplate(template, data);
  const lines: string[] = [];
  lines.push(`SIZE ${template.width_mm} mm, ${template.height_mm} mm`);
  lines.push('GAP 2 mm, 0 mm');
  lines.push('DIRECTION 0');
  // Niente REFERENCE: il firmware Clabel CT221B lo ignora; usiamo invece
  // l'offset additivo applicato a ciascun elemento sopra.
  lines.push('CLS');
  for (const el of rendered) {
    switch (el.type) {
      case 'text':
        lines.push(buildText(el));
        break;
      case 'barcode':
        lines.push(buildBarcode(el));
        break;
      case 'qr':
        lines.push(buildQr(el));
        break;
      case 'line':
        lines.push(buildBar(el));
        break;
      case 'rect':
        lines.push(buildBox(el));
        break;
      case 'expiry_weekday':
        throw new Error('Il giorno di scadenza richiede la stampa raster: seleziona un driver compatibile.');
      case 'image':
        break;
      default:
        break;
    }
  }
  lines.push(`PRINT ${Math.max(1, copies)},1`);
  return lines.join('\r\n') + '\r\n';
};

export class TsplAdapter implements PrinterAdapter {
  readonly driver = 'tspl' as const;

  async discover(): Promise<PrinterDevice[]> {
    if (useClassic()) {
      const paired = await classicListPaired();
      return paired.map((d) => ({
        id: d.address,
        name: d.name,
        driver: 'tspl' as const,
        connection: 'bluetooth' as const,
        address: d.address,
        model_hint: null,
      }));
    }
    const found = await bleScanPrinters(6_000);
    return found.map((d) => ({
      id: d.deviceId,
      name: d.name,
      driver: 'tspl' as const,
      connection: 'bluetooth' as const,
      address: d.deviceId,
      model_hint: null,
    }));
  }

  async connect(device: PrinterDevice): Promise<PrinterSession> {
    if (isY428Printer(device.name) && (device.connection !== 'bluetooth' || !useClassic(device.connection))) {
      throw new Error('Y428BT: usa Bluetooth Classic nell’app Android. Dal Mac usa la stampante di sistema USB.');
    }
    // T-081 follow-up: 'ble' usa BleClient; 'bluetooth' (= SPP) + Android
    // usa BluetoothSerial. La discriminazione passa via device.connection
    // letto da `printers.connection` (salvato dal pair flow).
    if (device.connection === 'ble') {
      await bleConnect(device.address);
    } else if (device.connection === 'bluetooth') {
      if (useClassic(device.connection)) await classicConnect(device.address);
      else await bleConnect(device.address);
    }
    return { deviceId: device.address, driver: this.driver, connection: device.connection,
      dpi: tsplProfile(device.name, device.dpi).dpi, model_hint: device.name };
  }

  async disconnect(_session: PrinterSession): Promise<void> {
    // No-op: manteniamo la sessione aperta tra stampe consecutive così la
    // stampante non va in standby tra un job e l'altro. La chiusura vera è
    // demandata a forceDisconnect (chiamato solo da "Dimentica stampante").
  }

  async forceDisconnect(session: PrinterSession): Promise<void> {
    const conn = (session as PrinterSession & { connection?: string }).connection;
    if (useClassic(conn)) await classicForceDisconnect(session.deviceId);
    else await bleDisconnect(session.deviceId);
  }

  async printLabel(
    session: PrinterSession,
    template: LabelTemplate,
    data: Record<string, unknown>,
    copies: number = 1,
  ): Promise<void> {
    // Stampa RASTER: l'etichetta è renderizzata su canvas (font liberi,
    // accenti, QR esatto) e inviata come immagine 1-bit col comando BITMAP.
    // Sostituisce i comandi TEXT/QRCODE (font ROM fissi). Vedi labelRaster.ts.
    const conn = (session as PrinterSession & { connection?: string }).connection;
    let bytes: Uint8Array;
    try {
      bytes = buildTsplBitmapJob(template, data, copies, tsplProfile(session.model_hint ?? '', session.dpi));
    } catch (error) {
      throw new LabelPrintStageError('render', error);
    }
    try {
      if (useClassic(conn)) await classicWriteBytes(session.deviceId, bytes);
      else await bleWriteBytes(session.deviceId, bytes);
    } catch (error) {
      throw new LabelPrintStageError('write', error);
    }
  }

  async getStatus(_session: PrinterSession): Promise<PrinterStatus> {
    return {
      online: true,
      paper_ok: true,
      cover_closed: true,
      battery_pct: null,
      error_code: null,
    };
  }
}
